import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ScoreGauge from "@/components/ScoreGauge";
import Logo from "@/components/Logo";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { API } from "@/lib/api";

const SUB_LABELS = {
  anlasilabilirlik: "Anlaşılabilirlik",
  bulunabilirlik: "Bulunabilirlik",
  onerilebilirlik: "Önerilebilirlik",
  icerik_gucu: "İçerik Gücü",
  guven_sinyali: "Güven Sinyali",
};

export default function SampleReport() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/sample-report`).then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor...</div>;

  const { business, latest_scan, tasks } = data;

  return (
    <div className="min-h-screen">
      <header className="px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to="/"><Logo /></Link>
        <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-sm text-white font-medium" data-testid="sample-cta">
          Kendi Raporumu Oluştur →
        </Link>
      </header>

      <main className="px-6 lg:px-12 py-8 max-w-6xl mx-auto space-y-6">
        <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full glass">
          <span className="text-cyan-400">●</span>
          <span className="text-slate-300">Örnek Rapor · Demo</span>
        </div>

        <div>
          <h1 className="font-display text-4xl font-bold text-white">{business.name}</h1>
          <p className="text-slate-400 mt-1">{business.sector} · {business.city}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 glass-strong rounded-3xl p-7">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">AI Görünürlük Skoru</div>
            <ScoreGauge score={latest_scan.overall_score} />
          </div>
          <div className="lg:col-span-2 glass rounded-3xl p-7">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">Alt skorlar</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(latest_scan.subscores).map(([k, v]) => (
                <div key={k}>
                  <div className="flex items-center justify-between mb-1.5 text-sm">
                    <span className="text-slate-300">{SUB_LABELS[k]}</span>
                    <span className="font-mono text-white">{v}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-purple-500" style={{ width: `${v}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-400 mt-5 border-t border-white/5 pt-4">{latest_scan.score_explanation}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="glass rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-3"><CheckCircle2 size={16} className="text-emerald-400" /><h3 className="font-display font-semibold text-white">Güçlü Yönler</h3></div>
            <ul className="space-y-2 text-sm text-slate-300">
              {latest_scan.strengths.map((s, i) => <li key={i}>• {s}</li>)}
            </ul>
          </div>
          <div className="glass rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-3"><AlertCircle size={16} className="text-amber-400" /><h3 className="font-display font-semibold text-white">Zayıf Yönler</h3></div>
            <ul className="space-y-2 text-sm text-slate-300">
              {latest_scan.weaknesses.map((s, i) => <li key={i}>• {s}</li>)}
            </ul>
          </div>
        </div>

        <div className="glass-strong rounded-3xl p-7">
          <h3 className="font-display text-2xl font-semibold text-white mb-4">AI Soru Hazırlık Testi</h3>
          <div className="space-y-2">
            {latest_scan.ai_question_tests.map((q, i) => (
              <div key={i} className="rounded-xl p-4 bg-white/[0.02] border border-white/5 flex items-start justify-between gap-4">
                <div>
                  <div className="text-white text-sm">"{q.question}"</div>
                  <div className="text-xs text-slate-500 mt-1">{q.reason}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border shrink-0 ${
                  q.readiness === "Güçlü" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                  : q.readiness === "Orta" ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                  : q.readiness === "Zayıf" ? "bg-orange-500/15 text-orange-300 border-orange-500/30"
                  : "bg-red-500/15 text-red-300 border-red-500/30"
                }`}>{q.readiness}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-strong rounded-3xl p-7">
          <h3 className="font-display text-2xl font-semibold text-white mb-4">Önerilen görevler</h3>
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.id} className="rounded-xl p-4 bg-white/[0.02] border border-white/5">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="font-medium text-white">{t.title}</div>
                  <span className="text-xs text-cyan-300 font-mono">+{t.impact_points} puan</span>
                </div>
                <div className="text-xs text-slate-500">{t.category}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center py-10 glass-strong rounded-3xl">
          <h3 className="font-display text-2xl font-bold text-white mb-2">Kendi raporunuzu görmeye hazır mısınız?</h3>
          <p className="text-slate-400 mb-6">5 dakikada ücretsiz analiz</p>
          <Link to="/kayit" className="neon-btn rounded-xl px-7 py-3.5 text-white font-semibold inline-flex items-center gap-2">
            Ücretsiz Skorumu Ölç <ArrowRight size={16} />
          </Link>
        </div>
      </main>
    </div>
  );
}
